import { AlertCircle, BookOpen, Building2, Calendar, Check, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkButton } from '../../components/BookmarkButton';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

interface Tenant {
  id: string;
  name: string;
  code: string;
  logoUrl?: string | null;
  nameI18n?: Record<string, string>;
  // Mocking period status for demo
  status?: 'before' | 'active' | 'ended';
}

// Simple debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

export const ApplicationWizardPage = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [isLoading, setIsLoading] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());

  // Fetch Bookmarks on mount
  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
        // Check if we have a token
        const localAuth = localStorage.getItem('admission-system-auth');
        if (!localAuth) return;

        const { accessToken } = JSON.parse(localAuth);
        const res = await fetch(`${apiBase}/api/bookmarks`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (res.ok) {
          const json = (await res.json()) as { data: Array<{ targetType: string; targetId: string }> };
          const ids = new Set<string>();
          json.data.forEach((b) => {
            if (b.targetType === 'tenant') ids.add(b.targetId);
          });
          setBookmarkedIds(ids);
        }
      } catch (e) {
        console.error('Failed to fetch bookmarks', e);
      }
    };
    fetchBookmarks();
  }, []);

  // Fetch Tenants when query changes
  useEffect(() => {
    const fetchTenants = async () => {
      setIsLoading(true);
      try {
        const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8787';
        const params = new URLSearchParams();
        if (debouncedQuery) params.append('q', debouncedQuery);

        const res = await fetch(`${apiBase}/api/public/tenants?${params}`);
        if (res.ok) {
          const data = await res.json();
          // Mocking period logic: Akita Uni (active), others might be distinct
          setTenants(
            data.data.map((t: Tenant) => ({
              ...t,
              status: t.code === 'akita-u' ? 'active' : 'before', // Demo: Akita U is active, others are 'before'
            })) || [],
          );
        }
      } catch (e) {
        console.error('Failed to fetch tenants', e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTenants();
  }, [debouncedQuery]);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => s - 1);

  const handleTenantSelect = (tenantId: string) => {
    if (selectedTenantId === tenantId) setSelectedTenantId('');
    else setSelectedTenantId(tenantId);
  };

  const toggleBookmarkState = (id: string, isBookmarked: boolean) => {
    const newSet = new Set(bookmarkedIds);
    if (isBookmarked) newSet.add(id);
    else newSet.delete(id);
    setBookmarkedIds(newSet);
  };

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId);
  const isPeriodActive = selectedTenant?.status === 'active';
  const isPeriodBefore = selectedTenant?.status === 'before';
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8 text-gray-900">{t('application.wizardTitle')}</h1>

      {/* Progress Bar */}
      <div className="mb-10">
        <div className="bg-gray-100 h-2.5 rounded-full overflow-hidden">
          <div
            className="bg-blue-600 h-full transition-all duration-500 ease-out"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-sm text-gray-500 font-medium">
          <span className={step >= 1 ? 'text-blue-600' : ''}>
            {t('application.searchPlaceholder').split(' ')[0] || 'Select'}
          </span>{' '}
          {/* Hacky translation usage */}
          <span className={step >= 2 ? 'text-blue-600' : ''}>{t('application.selectFaculty')}</span>
          <span className={step >= 3 ? 'text-blue-600' : ''}>{t('application.personalInfo')}</span>
          <span className={step >= 4 ? 'text-blue-600' : ''}>{t('application.photoUpload')}</span>
        </div>
      </div>

      <Card className="shadow-lg border-gray-200">
        <CardHeader className="border-b bg-gray-50/50 pb-6">
          <CardTitle className="text-xl">
            {step === 1 && (
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5 text-gray-500" />
                {t('dashboard.findUniversity')}
              </div>
            )}
            {step === 2 && t('application.selectFaculty')}
            {step === 3 && t('application.personalInfo')}
            {step === 4 && t('application.photoUpload')}
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 min-h-[400px]">
          {step === 1 && (
            <div className="space-y-6">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder={t('application.searchPlaceholder')}
                  className="pl-10 h-12 text-lg shadow-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {isLoading ? (
                <div className="py-12 text-center text-gray-500">Loading...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {tenants.map((tenant) => (
                    <div key={tenant.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => handleTenantSelect(tenant.id)}
                        className={`w-full text-left p-4 border rounded-xl transition-all duration-200 hover:shadow-md ${
                          selectedTenantId === tenant.id
                            ? 'border-blue-600 bg-blue-50/50 ring-1 ring-blue-600'
                            : 'hover:border-blue-300 border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-lg border border-gray-100 flex items-center justify-center shadow-sm overflow-hidden flex-shrink-0">
                              {tenant.logoUrl ? (
                                <img src={tenant.logoUrl} alt={tenant.name} className="w-full h-full object-contain" />
                              ) : (
                                <Building2 className="w-6 h-6 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                                {tenant.name}
                              </h3>
                              <Badge variant="secondary" className="mt-1 text-xs font-normal text-gray-500">
                                {tenant.code}
                              </Badge>
                              {/* Status Badge */}
                              <div className="mt-2">
                                {tenant.status === 'active' && (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none hover:bg-emerald-100">
                                    {t('application.period.during')}
                                  </Badge>
                                )}
                                {tenant.status === 'before' && (
                                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 shadow-none hover:bg-amber-100">
                                    {t('application.period.before')}
                                  </Badge>
                                )}
                                {tenant.status === 'ended' && (
                                  <Badge className="bg-gray-100 text-gray-500 border-gray-200 shadow-none hover:bg-gray-100">
                                    {t('application.period.ended')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Spacer to prevent overlap with absolute elements */}
                          <div className="w-16" />
                        </div>
                      </button>

                      {/* Bookmark Button - Absolute Positioned */}
                      <div className="absolute top-4 right-4 z-10 flex gap-2">
                        <BookmarkButton
                          targetType="tenant"
                          targetId={tenant.id}
                          tenantId={tenant.id}
                          initialIsBookmarked={bookmarkedIds.has(tenant.id)}
                          onToggle={(val) => toggleBookmarkState(tenant.id, val)}
                        />
                      </div>

                      {/* Check Icon */}
                      {selectedTenantId === tenant.id && (
                        <div className="absolute top-14 right-4 animate-in fade-in zoom-in duration-200 pointer-events-none">
                          <div className="bg-blue-600 text-white rounded-full p-1 shadow-lg">
                            <Check className="w-4 h-4" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {tenants.length === 0 && !isLoading && (
                    <div className="col-span-full py-12 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed">
                      {t('application.noResults')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="p-5 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors group">
                <h3 className="font-medium text-lg text-blue-900 group-hover:text-blue-700">
                  Faculty of International Resource Sciences
                </h3>
                <p className="text-sm text-gray-500 mt-1">Resource Policy and Management, Earth System Science</p>
              </div>
              <div className="p-5 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors group">
                <h3 className="font-medium text-lg text-blue-900 group-hover:text-blue-700">
                  Faculty of Education and Human Studies
                </h3>
                <p className="text-sm text-gray-500 mt-1">School Education, Regional Culture</p>
              </div>
              <div className="p-5 border rounded-xl hover:bg-gray-50 cursor-pointer transition-colors group">
                <h3 className="font-medium text-lg text-blue-900 group-hover:text-blue-700">Faculty of Medicine</h3>
                <p className="text-sm text-gray-500 mt-1">Medicine, Health Sciences</p>
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input placeholder="Taro" className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input placeholder="Yamada" className="h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" className="h-11" />
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-16 hover:bg-gray-50 transition-colors cursor-pointer group">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
                  <Search className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900">Upload ID Photo</p>
                  <p className="text-sm text-gray-500 mt-1">Drag and drop or click to browse</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col gap-4 border-t bg-gray-50/50 py-6">
          {/* Period Notices */}
          {step === 1 && selectedTenant && isPeriodBefore && (
            <div className="w-full flex items-center gap-3 p-4 bg-amber-50 text-amber-800 rounded-lg border border-amber-200">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-bold mb-1">{t('application.period.message')}</p>
              </div>
            </div>
          )}

          <div className="w-full flex justify-between items-center">
            <Button variant="outline" onClick={prevStep} disabled={step === 1} className="h-11 px-8">
              {t('common.back')}
            </Button>

            <div className="flex gap-3">
              {/* Actions for Pre-Period */}
              {step === 1 && isPeriodBefore && (
                <>
                  <Button variant="outline" className="h-11 border-blue-200 text-blue-700 hover:bg-blue-50">
                    <BookOpen className="mr-2 h-4 w-4" />
                    {t('dashboard.actions.requestMaterials')}
                  </Button>
                  {!bookmarkedIds.has(selectedTenantId) && (
                    <Button
                      variant="secondary"
                      className="h-11"
                      onClick={() => selectedTenantId && toggleBookmarkState(selectedTenantId, true)}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      Bookmark
                    </Button>
                  )}
                </>
              )}

              <Button
                onClick={nextStep}
                disabled={step === 1 && (!selectedTenantId || !isPeriodActive)}
                className="h-11 px-8 bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all"
              >
                {step === 4 ? t('common.submit') : t('common.next')}
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};
